import * as React from "react";
import classNames from "classnames";
import {
	WithStyles,
	Theme,
	createStyles,
	withStyles,
	ListItemSecondaryAction
} from "@material-ui/core";
import { connect } from "react-redux";

import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import PeopleIcon from "@material-ui/icons/People";
import AssignmentIcon from "@material-ui/icons/Assignment";
import RestaurantIcon from "@material-ui/icons/Restaurant";

import { Dispatch } from "redux";
import { StoreState } from "../../reducers/root-reducer";
import { ConnectedSheet } from "../sheet";
import { formatDate } from "../../util/date-utility";
import { Reservation } from "../../api/reservations";
import { OccupiedDay } from "../../api/occupied-days";
import { Table } from "../../api/tables";
import {
	getTimeFilteredReservations,
	getSelectedOccupiedDay,
	getReservationsForDay
} from "../../util/general-utility";
import { ConnectedLinearProgressbar } from "../layout/linear-progressbar";
import { Area } from "../../api/areas";
import { ConnectedMiniPlan } from "./mini-plan";
import { InjectedRouterProps } from "../../router/props";
import { Routes } from "../../router/routes";
import { withRouter } from "react-router-dom";
import { setActiveArea } from "../../actions/store/overview-actions";

interface State {
	totalTables: number;
	occupiedTables: number;
	peopleCount: number;
	totalSoftLimit: number;
	sortedReservations: number;
	unsortedReservations: number;
	occupiedTableIds: string[];
	isLockdown: boolean;
	lockedAreas: string[];
}

export interface DayOccupancySummaryProps {
	title: string;
	area?: Area;
}

interface ConnectedDayOccupancySummaryProps extends WithStyles<typeof styles> {
	selectedDate: Date;
	tables: Table[];
	reservations: Reservation[];
	occupiedDays: OccupiedDay[];
	selectedTime: string;
	layout: "desktop" | "tablet" | "phone";
	setActiveArea: (id: string) => void;
}

type Props = DayOccupancySummaryProps & ConnectedDayOccupancySummaryProps & InjectedRouterProps;

export class DayOccupancySummary extends React.Component<Props, State> {
	public state: State = {
		totalTables: 0,
		occupiedTables: 0,
		peopleCount: 0,
		totalSoftLimit: 0,
		sortedReservations: 0,
		unsortedReservations: 0,
		occupiedTableIds: [],
		isLockdown: true,
		lockedAreas: []
	};

	public componentDidMount() {
		this.getTableOccupancySummary();
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (
			nextProps.occupiedDays !== this.props.occupiedDays ||
			nextProps.reservations !== this.props.reservations ||
			nextProps.selectedDate !== this.props.selectedDate ||
			nextProps.selectedTime !== this.props.selectedTime
		) {
			this.getTableOccupancySummary(nextProps);
		}
	}

	public shouldComponentUpdate(nextProps: Props, nextState: State) {
		return nextState !== this.state;
	}

	private getTableOccupancySummary = (props = this.props) => {
		const {
			selectedDate,
			reservations,
			tables,
			occupiedDays,
			area,
			selectedTime
		} = props;

		const tableIds: string[] = [];
		let totalPeopleCount = 0;
		let totalSoftLimit = 0;
		let sortedReservations = 0;
		let unsortedReservations = 0;

		let filteredTables = tables;

		const day = getSelectedOccupiedDay(occupiedDays, selectedDate);
		let selectedReservations = getReservationsForDay(
			reservations,
			selectedDate
		);

		// Filter By Time
		let filteredReservations: Array<Reservation> = getTimeFilteredReservations(selectedReservations, selectedTime);

		if (area) {
			filteredTables = tables.filter(table => table.areaId === area.id);

			filteredReservations = filteredReservations.filter(res => {
				if (res.tableId) {
					return filteredTables.some(
						table => table.id === res.tableId
					);
				} else {
					return true;
				}
			});
		}

		if (reservations.length > 0 && occupiedDays.length > 0) {
			filteredReservations.forEach(res => {
				if (res.tableId) {
					day.linkedTables.forEach(group => {
						if (group.indexOf(res.tableId) > -1) {
							group.forEach(id => {
								if (tableIds.indexOf(id) === -1) {
									tableIds.push(id);
								}
							});
						}
					});

					if (tableIds.indexOf(res.tableId) === -1) {
						tableIds.push(res.tableId);
					}

					sortedReservations++;
					totalPeopleCount += res.peopleCount;
				} else {
					unsortedReservations++;

					if (!area) {
						totalPeopleCount += res.peopleCount;
					}
				}
			});
		}

		totalSoftLimit = filteredTables.reduce((sum, table) => {
			if (area) {
				return table.areaId === area.id
					? sum + table.peopleSoftLimit
					: sum;
			} else {
				return sum + table.peopleSoftLimit;
			}
		}, 0);

		this.setState({
			totalTables: filteredTables.length,
			occupiedTables: tableIds.length,
			peopleCount: totalPeopleCount,
			totalSoftLimit,
			sortedReservations,
			unsortedReservations,
			occupiedTableIds: tableIds,
			isLockdown: (day && day.lockdown),
			lockedAreas: (day && day.lockedAreas) ? day.lockedAreas : []
		});
	};

	private handleAreaClick = () => {
		const { history, layout, area, setActiveArea } = this.props;
 
		setActiveArea(area.id);

		if (layout === "phone") {
			this.props.history.push(Routes.BookingPlanList);
		} else {
			this.props.history.push(Routes.BookingPlanComposer);
		}
	};

	public render() {
		const { classes, title, area } = this.props;
		const {
			totalTables,
			occupiedTables,
			peopleCount,
			totalSoftLimit,
			sortedReservations,
			unsortedReservations,
			occupiedTableIds,
			isLockdown,
			lockedAreas
		} = this.state;

		if (totalTables === 0) return null;

		return (
			<Paper>
				<div 
					className={classNames(
						classes.primaryContent,
						classes.areaTitle
					)}
					onClick={this.handleAreaClick} >
					<Typography
						variant="title"
						component="span"
						className={classes.title}
					>
						{title}
					</Typography>
				</div>
				{area && (
					<div className={classes.secondaryContent}>
						<ConnectedMiniPlan
							area={area}
							occupiedTableIds={occupiedTableIds}
							locked={isLockdown || lockedAreas.indexOf(area.id) > -1}
						/>
					</div>
				)}
				<div className={classes.primaryContent}>
					<div className={classes.group}>
						<div className={classes.groupIcon}>
							<RestaurantIcon className={classes.icon} />
						</div>
						<div className={classes.groupContent}>
							<ConnectedLinearProgressbar
								progress={occupiedTables / totalTables}
							/>
							<Typography>
								<span className={classes.strong}>
									{occupiedTables} von {totalTables}
								</span>{" "}
								Tischen belegt
							</Typography>
						</div>
					</div>
					<div className={classes.group}>
						<div className={classes.groupIcon}>
							<AssignmentIcon className={classes.icon} />
						</div>
						<div className={classes.groupContent}>
							<ConnectedLinearProgressbar
								progress={
									sortedReservations /
									(sortedReservations + unsortedReservations)
								}
							/>
							<Typography>
								<span className={classes.strong}>
									{sortedReservations} von{" "}
									{sortedReservations + unsortedReservations}
								</span>{" "}
								Reservierungen eingeteilt
							</Typography>
						</div>
					</div>
					<div className={classes.group}>
						<div className={classes.groupIcon}>
							<PeopleIcon className={classes.icon} />
						</div>
						<div className={classes.groupContent}>
							<ConnectedLinearProgressbar
								progress={peopleCount / totalSoftLimit}
							/>
							<Typography>
								<span className={classes.strong}>
									{Math.round(
										(peopleCount * 100) / totalSoftLimit
									)}
									%
								</span>{" "}
								Auslastung ({peopleCount} Personen)
							</Typography>
						</div>
					</div>
				</div>
			</Paper>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	selectedDate: state.date,
	tables: state.tables,
	reservations: state.reservations,
	selectedTime: state.overviewActiveTimeId,
	occupiedDays: state.occupiedDays,
	layout: state.layout
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setActiveArea: (id: string) => dispatch(setActiveArea(id))
});

const styles = (theme: Theme) =>
	createStyles({
		title: {
			fontSize: "1.2rem"
		},
		strong: {
			fontWeight: 500
		},
		primaryContent: {
			padding: 20
		},
		areaTitle: {
			cursor: "pointer"
		},
		secondaryContent: {
			padding: 20,
			paddingTop: 24,
			background: "#FAFAFA"
		},
		group: {
			display: "flex",
			alignItems: "center",

			"& + &": {
				marginTop: 15
			}
		},
		groupIcon: {
			paddingRight: 15
		},
		groupContent: {
			flexGrow: 1,
			marginTop: 2
		},
		icon: {
			color: "#666666"
		}
	});

export const ConnectedDayOccupancySummary = withRouter(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(DayOccupancySummary)
	)
);
